import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { ShieldAlert } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { Button } from '@/components/ui'
import { useT } from '@/i18n/I18nProvider'

/**
 * The strip that says somebody else is signed in as you.
 *
 * Shown to **both** people, and that is the design rather than an oversight.
 * The owner needs it because an unmarked support session is how a change gets
 * made in the wrong account; the customer's user needs it because a capability
 * they were told about is one they can tolerate, and the same capability found
 * out about later is a breach of trust regardless of what it was used for.
 *
 * So it is loud, fixed to the top of the viewport, and has no dismiss control.
 * Being able to close it would make "was support in my account" a question
 * about whether somebody had clicked an ✕, which is exactly the doubt the
 * banner exists to remove.
 */
export function ImpersonationBanner() {
  const { impersonation, user, stopImpersonation } = useAuth()
  const t = useT()
  const [leaving, setLeaving] = useState(false)
  const navigate = useNavigate()

  if (!impersonation) return null

  const leave = async () => {
    setLeaving(true)

    try {
      await stopImpersonation()
      navigate('/vlasnik', { replace: true })
      toast.success(t('support.ended'))
    } catch {
      toast.error(t('support.endFailed'))
    } finally {
      setLeaving(false)
    }
  }

  return (
    <div
      /*
       * `role="status"` rather than `alert`: it is a standing condition, not an
       * event, and an assertive live region would interrupt a screen reader
       * mid-sentence on every route change.
       */
      role="status"
      /*
       * Not sticky itself — AppShell sticks the header stack this sits inside,
       * so the banner and the navigation bar pin as one block instead of
       * fighting over the same strip at `top-0`.
       */
      className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 border-b border-amber-500/30 bg-amber-500/15 px-4 py-2.5 text-sm backdrop-blur-xl backdrop-saturate-150 supports-[backdrop-filter]:bg-amber-500/10 dark:border-amber-400/25"
    >
      <ShieldAlert className="size-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />

      <p className="text-center text-amber-900 dark:text-amber-100">
        <span className="font-semibold">
          {impersonation.ownerName ?? t('support.badge')}
        </span>{' '}
        je prijavljen/a kao{' '}
        <span className="font-semibold">{user?.name}</span>. Sve izmjene se bilježe.
      </p>

      <Button
        variant="secondary"
        onClick={leave}
        loading={leaving}
        className="min-h-9 rounded-full px-3 py-1 text-xs"
      >
        {t('support.leave')}
      </Button>
    </div>
  )
}
